import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { CloudFramePrimaryButton } from "@cloudframe/button";
import { CloudFrameDropdown } from "@cloudframe/dropdown";
import { CloudFrameTextBox } from "@cloudframe/textbox";
import { IDropdownProps, IIconProps, Icon, TextField } from "@fluentui/react";
import { useEffect, useMemo, useState } from "react";
import {
  MUTATION_UPDATE_NOTIFICATION_CRON_CONFIG,
  MUTATION_UPSERT_NOTIFICATION_RECIPIENTS,
} from "../../graphql-mutations/continuum-scanner-notification.mutation";
import {
  QUERY_GET_NOTIFICATION_CRON_CONFIG,
  QUERY_GET_NOTIFICATION_RECIPIENTS,
} from "../../graphql-query/continuum-scanner-notification.query";
import {
  QUERY_GET_ALL_CONTINUUM_LICENSES,
  QUERY_GET_ALL_SCANNER_LICENSES,
} from "../../graphql-query/continuum-scanner-license.query";
import { useStyles } from "./continuum-scanner-notification-settings.styles";

const PRODUCT_OPTIONS: IDropdownProps["options"] = [
  { key: "Continuum", text: "Continuum" },
  { key: "Scanner", text: "Scanner" },
];

const addIcon: IIconProps = { iconName: "Add" };

const roundedFieldStyles = {
  fieldGroup: { borderRadius: "8px", height: "40px" },
  field: { fontSize: "14px" },
};
const roundedDropdownStyles = {
  title: { borderRadius: "8px", height: "40px", lineHeight: "38px" },
  dropdown: { borderRadius: "8px" },
};
const roundedButtonStyles = { root: { borderRadius: "8px", height: "40px" } };

const parseRecipients = (value: string): string[] =>
  value
    .split(",")
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContinuumScannerNotificationSettings = () => {
  const classes = useStyles();

  const [cronIntervalHours, setCronIntervalHours] = useState<number>(24);
  const [intervalSaved, setIntervalSaved] = useState(false);
  const [intervalError, setIntervalError] = useState<string | null>(null);

  const [product, setProduct] = useState<string>("Continuum");
  const [licenseId, setLicenseId] = useState("");
  const [licenseSearchText, setLicenseSearchText] = useState("");
  const [savedRecipients, setSavedRecipients] = useState<string[]>([]);
  const [pendingRecipients, setPendingRecipients] = useState<string[]>([]);
  const [newRecipientInput, setNewRecipientInput] = useState("");
  const [newRecipientError, setNewRecipientError] = useState<string | null>(null);
  const [recipientsSaved, setRecipientsSaved] = useState(false);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);

  const { data: cronConfigData } = useQuery(QUERY_GET_NOTIFICATION_CRON_CONFIG);
  const [updateCronConfig] = useMutation(MUTATION_UPDATE_NOTIFICATION_CRON_CONFIG);

  const { data: continuumLicensesData } = useQuery(QUERY_GET_ALL_CONTINUUM_LICENSES);
  const { data: scannerLicensesData } = useQuery(QUERY_GET_ALL_SCANNER_LICENSES);
  const [loadRecipients, { data: recipientsData }] = useLazyQuery(
    QUERY_GET_NOTIFICATION_RECIPIENTS,
    { fetchPolicy: "network-only" }
  );
  const [upsertRecipients] = useMutation(MUTATION_UPSERT_NOTIFICATION_RECIPIENTS);

  const continuumLicenses = continuumLicensesData?.getAllContinuumLicenses || [];
  const scannerLicenses = scannerLicensesData?.getAllScannerLicenses || [];
  const licensesForProduct = product === "Continuum" ? continuumLicenses : scannerLicenses;

  const licenseOptions: IDropdownProps["options"] = useMemo(
    () =>
      licensesForProduct.map((license: any) => ({
        key: license.licenseId,
        text: license.licenseId,
      })),
    [licensesForProduct]
  );

  const filteredLicenseOptions: IDropdownProps["options"] = useMemo(() => {
    const search = licenseSearchText.trim().toLowerCase();
    if (!search) return licenseOptions;
    return licenseOptions.filter((option) =>
      String(option.text).toLowerCase().includes(search)
    );
  }, [licenseOptions, licenseSearchText]);

  const selectedLicenseEmail: string | null = useMemo(() => {
    const license = licensesForProduct.find((l: any) => l.licenseId === licenseId);
    return license?.email ?? null;
  }, [licensesForProduct, licenseId]);

  const allSavedChips: string[] = useMemo(() => {
    const chips = [...savedRecipients];
    if (selectedLicenseEmail && !chips.includes(selectedLicenseEmail)) {
      chips.unshift(selectedLicenseEmail);
    }
    return chips;
  }, [savedRecipients, selectedLicenseEmail]);

  useEffect(() => {
    if (cronConfigData?.getNotificationCronConfig?.cronIntervalHours != null) {
      setCronIntervalHours(cronConfigData.getNotificationCronConfig.cronIntervalHours);
    }
  }, [cronConfigData]);

  useEffect(() => {
    setLicenseId("");
    setSavedRecipients([]);
    setPendingRecipients([]);
  }, [product]);

  useEffect(() => {
    if (!licenseId) {
      setSavedRecipients([]);
      setPendingRecipients([]);
      return;
    }
    setPendingRecipients([]);
    setRecipientsSaved(false);
    setRecipientsError(null);
    loadRecipients({ variables: { licenseId } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  useEffect(() => {
    setSavedRecipients(
      parseRecipients(recipientsData?.getNotificationRecipients?.extraRecipients || "")
    );
  }, [recipientsData]);

  useEffect(() => {
    if (!intervalSaved) return;
    const timer = setTimeout(() => setIntervalSaved(false), 4000);
    return () => clearTimeout(timer);
  }, [intervalSaved]);

  useEffect(() => {
    if (!recipientsSaved) return;
    const timer = setTimeout(() => setRecipientsSaved(false), 4000);
    return () => clearTimeout(timer);
  }, [recipientsSaved]);

  const handleSaveInterval = async () => {
    setIntervalSaved(false);
    if (!Number.isInteger(cronIntervalHours) || cronIntervalHours <= 0) {
      setIntervalError("The number of hours should be greater than 0.");
      return;
    }
    setIntervalError(null);
    try {
      await updateCronConfig({ variables: { cronIntervalHours } });
      setIntervalSaved(true);
    } catch (err: any) {
      setIntervalError(err.message);
    }
  };

  const handleAddRecipient = () => {
    const email = newRecipientInput.trim();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      setNewRecipientError("Enter a valid email address.");
      return;
    }
    if (savedRecipients.includes(email) || pendingRecipients.includes(email)) {
      setNewRecipientError("This recipient has already been added.");
      setNewRecipientInput("");
      return;
    }
    setPendingRecipients([...pendingRecipients, email]);
    setNewRecipientInput("");
    setNewRecipientError(null);
  };

  const handleRemovePending = (email: string) => {
    setPendingRecipients(pendingRecipients.filter((r) => r !== email));
  };

  const handleSaveRecipients = async () => {
    if (!licenseId) return;

    // Anything still sitting in the "Add recipient" box hasn't been added yet.
    // Validate it rather than silently dropping it (or silently letting it through).
    const leftover = newRecipientInput.trim();
    let recipientsToSave = [...savedRecipients, ...pendingRecipients];
    if (leftover) {
      if (!EMAIL_REGEX.test(leftover)) {
        setNewRecipientError("Enter a valid email address before saving.");
        return;
      }
      if (!recipientsToSave.includes(leftover)) {
        recipientsToSave = [...recipientsToSave, leftover];
      }
    }

    setRecipientsError(null);
    setRecipientsSaved(false);
    try {
      const allRecipients = recipientsToSave.join(",");
      await upsertRecipients({
        variables: { licenseId, product, extraRecipients: allRecipients },
      });
      setSavedRecipients(recipientsToSave);
      setPendingRecipients([]);
      setNewRecipientInput("");
      setNewRecipientError(null);
      setRecipientsSaved(true);
    } catch (err: any) {
      setRecipientsError(err.message);
    }
  };

  return (
    <div className={classes.root}>
      <h2 className={classes.pageTitle}>Notification settings</h2>
      <p className={classes.pageSubtitle}>
        Control expiry alerts and who gets notified per license.
      </p>

      <div className={classes.card}>
        <h3 className={classes.cardTitle}>Expiry notification interval</h3>
        <div className={classes.intervalRow}>
          <CloudFrameTextBox
            type="number"
            min={1}
            step={1}
            className={classes.intervalInput}
            styles={roundedFieldStyles}
            value={String(cronIntervalHours)}
            onChange={(_, v) => {
              setCronIntervalHours(Number(v) || 0);
              if (intervalError) setIntervalError(null);
              if (intervalSaved) setIntervalSaved(false);
            }}
          />
          <span className={classes.hoursLabel}>hours</span>
          <CloudFramePrimaryButton
            className={classes.saveIntervalButton}
            text="Save interval"
            styles={roundedButtonStyles}
            onClick={handleSaveInterval}
          />
        </div>
        {intervalSaved && (
          <div className={classes.successBanner}>Interval saved.</div>
        )}
        {intervalError && <div className={classes.errorBanner}>{intervalError}</div>}
      </div>

      <div className={classes.card}>
        <h3 className={classes.cardTitle}>License recipients</h3>
        <p className={classes.cardSubtitle}>
          Extra people notified about this license, on top of the default owner.
        </p>

        <span className={classes.fieldLabel}>License</span>
        <div className={classes.licenseRow}>
          <CloudFrameDropdown
            className={classes.licenseInput}
            styles={roundedDropdownStyles}
            placeholder="Select a license"
            options={filteredLicenseOptions}
            selectedKey={licenseId || null}
            onChange={(_, option) => option && setLicenseId(String(option.key))}
            onDismiss={() => setLicenseSearchText("")}
            calloutProps={{ calloutMaxHeight: 320 }}
            onRenderList={(listProps, defaultRender) => (
              <div>
                <div
                  className={classes.dropdownSearchBox}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <TextField
                    placeholder="Search licenses..."
                    value={licenseSearchText}
                    onChange={(_, v) => setLicenseSearchText(v || "")}
                    autoFocus
                  />
                </div>
                {defaultRender && defaultRender(listProps)}
              </div>
            )}
          />
          <CloudFrameDropdown
            className={classes.productDropdown}
            styles={roundedDropdownStyles}
            options={PRODUCT_OPTIONS}
            selectedKey={product}
            onChange={(_, option) => option && setProduct(String(option.key))}
          />
        </div>

        {licenseId && (
          <>
            {allSavedChips.length > 0 && (
              <>
                <span className={classes.fieldLabel}>Saved recipients for {licenseId}</span>
                <div className={classes.chipRow}>
                  {allSavedChips.map((email) => (
                    <span key={email} className={classes.chipSaved}>
                      {email}
                    </span>
                  ))}
                </div>
              </>
            )}

            {pendingRecipients.length > 0 && (
              <>
                <span className={classes.notSavedLabel}>Not saved yet</span>
                <div className={classes.chipRow}>
                  {pendingRecipients.map((email) => (
                    <span key={email} className={classes.chipPending}>
                      {email}
                      <Icon
                        iconName="Cancel"
                        className={classes.chipRemoveIcon}
                        onClick={() => handleRemovePending(email)}
                      />
                    </span>
                  ))}
                </div>
              </>
            )}

            <span className={classes.fieldLabel}>Add recipient</span>
            <div className={classes.addRecipientRow}>
              <CloudFrameTextBox
                className={classes.addRecipientInput}
                styles={roundedFieldStyles}
                placeholder="name@company.com"
                value={newRecipientInput}
                onChange={(_, v) => {
                  setNewRecipientInput(v || "");
                  if (newRecipientError) setNewRecipientError(null);
                }}
              />
              <button
                type="button"
                className={classes.addRecipientButton}
                onClick={handleAddRecipient}
                disabled={!newRecipientInput.trim()}
              >
                <Icon iconName={addIcon.iconName} />
              </button>
            </div>
            {newRecipientError && (
              <span className={classes.inlineError}>{newRecipientError}</span>
            )}

            <div className={classes.btnRow}>
              <CloudFramePrimaryButton
                text="Save recipients"
                styles={roundedButtonStyles}
                onClick={handleSaveRecipients}
              />
            </div>
            {recipientsSaved && (
              <div className={classes.successBanner}>Recipients saved.</div>
            )}
            {recipientsError && <div className={classes.errorBanner}>{recipientsError}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default ContinuumScannerNotificationSettings;
